import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ANALYTICS_PATH } from '@constants/routes';
import { IoMdArrowRoundBack, IoMdCloseCircle } from 'react-icons/io';
import { FaArrowLeftLong } from 'react-icons/fa6';
import { hospitalUuid } from '@utils/hospitalUtils';
import { useAppSelector } from '@store/hooks';
import Select from 'react-select';
import {
    PutItemCommand,
    PutItemCommandInput,
    QueryCommand,
} from '@aws-sdk/client-dynamodb';
import { dbClient } from '@utils/awsClients';
import { v4 as uuidv4 } from 'uuid';
import DataLoader from '@components/DataLoader';
import Swal from 'sweetalert2';
import ErrorMessage from '@components/Error/ErrorMessage';
import {
    capitalizeFirstLetter,
    generateUniqueTimestamp,
    getDataUsingDeviceId,
    getDataUsingUserId,
    sendAssignNotification,
} from '@utils/sharedUtils';
import { useTranslation } from 'react-i18next';
import { useAuditLog } from '../contexts/AuditLogContext';
import { sendPushNotification } from '@utils/notificationUtils';
import { NURSE_DEVICE_TABLE, USER_INFO_TABLE } from '@constants/dynamoDBTableName';

const MySwal = Swal;

interface Nurse {
    tenant_id: string;
    name: string;
    uuid: string;
    user_id: string;
    nurse_id?: string;
}

interface Device {
    uuid: string;
    device_id: string;
    device_name?: string;
    hospital_id: string;
}

interface ModalPopupProps {
    showModal: boolean;
    nurseData: Nurse[];
    deviceData: Device[];
    onClose: () => void;
    refreshData?: () => void;
}

const AssignModalPopUp: React.FC<ModalPopupProps> = ({
    showModal,
    nurseData,
    deviceData,
    onClose,
    refreshData = () => {},
}) => {
    if (!showModal) return null;

    const [inputValue, setInputValue] = useState('');
    const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
    const [selectedNurses, setSelectedNurses] = useState<Nurse[]>([]);
    const [filteredNurseData, setFilteredNurseData] = useState<Nurse[]>(nurseData);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { t } = useTranslation();
    const loggedInUserTenantID = useAppSelector(
        (state) => state.login.loggedInUserTenantID
    );
    const [error, setError] = useState<string | null>(null);
    const loggedInUserID = useAppSelector(
        (state) => state.login.loggedInUserID
    );
    const loggedInUserEmail = useAppSelector(
        (state) => state.login.loggedInUserEmail
    );
    const { addLog } = useAuditLog();

    // Update filtered nurse data when nurseData prop changes
    useEffect(() => {
        setFilteredNurseData(nurseData);
    }, [nurseData]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (!selectedDevice || selectedNurses.length == 0) {
                // console.error('Please select both device and nurse');
                return;
            }
            // const formattedDateTime = new Date().toISOString();
            const formattedDateTime = generateUniqueTimestamp();

            for (const nurse of selectedNurses) {
                const checkDeviceParams = {
                    TableName: 'nurse_device',
                    IndexName: 'device_id-created_at-index',
                    KeyConditionExpression: 'device_id = :device_id',
                    ExpressionAttributeValues: {
                        ':device_id': { S: selectedDevice.uuid },
                    },
                };
                const checkDeviceCommand = new QueryCommand(checkDeviceParams);
                const checkDeviceResponse =
                    await dbClient.send(checkDeviceCommand);

                let hasControl = true;
                let isAlreadyAssigned = false;
                if (
                    checkDeviceResponse.Items &&
                    checkDeviceResponse.Items.length > 0
                ) {
                    for (const item of checkDeviceResponse.Items) {
                        if (
                            item.device_id.S === selectedDevice.uuid &&
                            !item.deleted_at?.S
                        ) {
                            if (item.user_id.S === nurse.user_id) {
                                isAlreadyAssigned = true;
                                break;
                            }
                            if (item.has_control.BOOL) {
                                hasControl = false;
                            }
                        }
                    }
                }

                if (isAlreadyAssigned) {
                    setError(
                        'Device is already assigned to one of the selected nurses.'
                    );
                    return;
                }

                const id = uuidv4();
                const params: PutItemCommandInput = {
                    TableName: 'nurse_device',
                    Item: {
                        uuid: { S: id },
                        device_id: { S: selectedDevice.uuid },
                        hospital_id: { S: selectedDevice.hospital_id },
                        user_id: { S: nurse.user_id ? nurse.user_id : '' },
                        tenant_id: { S: loggedInUserTenantID },
                        has_control: { BOOL: hasControl },
                        created_at: { S: formattedDateTime },
                        created_by: { S: loggedInUserTenantID },
                        modified_at: { S: formattedDateTime },
                        modified_by: { S: loggedInUserTenantID },
                        deleted_at: { S: '' },
                        deleted_by: { S: '' },
                    },
                };

                const command = new PutItemCommand(params);
                await dbClient.send(command);

                const logData = nurse.user_id ? await getDataUsingUserId(
                                    USER_INFO_TABLE,
                                    nurse.user_id
                                ) : null;

                await sendPushNotification({user_id: nurse.user_id ? nurse.user_id : '', type: 'assigned_device', device_id: selectedDevice.device_id, reason: '', assigned_by: loggedInUserID });
                addLog(
                    'Device Assigned to user',
                    id,
                    'Nurse Device',
                    loggedInUserID,
                    { 'Device': selectedDevice.device_id, 'Assigned To': logData?.Items?.[0]?.email?.S },
                    window.location.hostname
                );

                const email = logData?.Items?.[0]?.email?.S;
                const toAddress = email ? [email] : [];

                const notificationData = {
                    type: 'email',
                    users: nurse.user_id, // Sending recipient email/phone/deviceToken based on type
                    fromAddress: loggedInUserEmail,
                    toAddress: nurse.user_id,
                    schedule: null, // Include schedule if applicable
                };
    
                // Send the data to the sharedUtils.ts function
                await sendAssignNotification(notificationData);
            }

            MySwal.fire({
                position: 'top-end', // Position the toast in the top-right corner
                icon: 'success',
                title: 'Device assigned successfully',
                showConfirmButton: false,
                timer: 1500,
                toast: true,
                customClass: {
                    popup: 'text-green bg-light_green', // Add custom class to the popup
                },
            });

            onClose();
            refreshData();
        } catch (error) {
            console.error('Error submitting assignment:', error);
        }
    };

    const handleDeviceChange = async (selectedOption: any) => {
        setSelectedDevice({
            uuid: selectedOption.value,
            device_id: selectedOption.label,
            hospital_id: selectedOption.hospitalId, // Assuming hospital_id needs to be set separately
        });

        // Filter out nurses who are already assigned to the selected device
        try {
            setLoading(true);
            setError(null);
            const assignedNursesResponse = await getDataUsingDeviceId(
                NURSE_DEVICE_TABLE,
                selectedOption.value
            );

            // Get user_ids of nurses already assigned to this device
            const assignedUserIds = assignedNursesResponse.Items?.filter(
                (item) => !item.deleted_at?.S
            ).map((item) => item.user_id?.S) || [];

            // Filter out nurses who are already assigned
            const filtered = nurseData.filter(
                (nurse) => !assignedUserIds.includes(nurse.user_id)
            );

            setFilteredNurseData(filtered);
            
            // Check if any of the currently selected nurses are already assigned to this device
            if (selectedNurses.length > 0) {
                // Remove only the invalid nurses from selection
                const validNurses = selectedNurses.filter(
                    (nurse) => !assignedUserIds.includes(nurse.user_id)
                );
                setSelectedNurses(validNurses);
            }
        } catch (error) {
            console.error('Error filtering nurses:', error);
            // On error, show all nurses
            setFilteredNurseData(nurseData);
            setError('Error checking device assignments. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleNurseChange = (selectedOptions: any) => {
        setSelectedNurses(
            selectedOptions.map((option: any) => ({
                uuid: option.value,
                name: option.label,
                user_id: option.userId,
                tenant_id: option.tenantId,
            }))
        );
    };

    return (
        <>
            <div className="fixed inset-0 flex items-center justify-center bg-gray-500 bg-opacity-75 z-50">
                <div className="bg-white p-6 rounded-lg shadow-lg relative">
                    <div className="text-center text-black text-lg font-semibold">
                        {t('management.assign.assign')}
                    </div>
                    <button
                        className="absolute top-0 right-0 mt-2 mr-2 text-black"
                        onClick={onClose}
                        aria-label="close"
                    >
                        <IoMdCloseCircle className="text-lg" />
                    </button>
                    <div className="my-4">
                        {error && (
                            <div className="w-max mx-auto">
                                <ErrorMessage message={error} />
                            </div>
                        )}
                        <div className="cart bg-white items-center justify-content-center rounded-2xl w-96 p-4 mx-auto">
                            <form
                                onSubmit={handleSubmit}
                                className="flex flex-col"
                            >
                                <div className="mb-4">
                                    <label
                                        htmlFor="device_id"
                                        className="block mb-1"
                                    >
                                        {t('management.assign.select_device')}
                                    </label>
                                    <Select
                                        id="device_id"
                                        name="device_id"
                                        options={deviceData.map((device) => ({
                                            value: device.uuid,
                                            label: `${device.device_name} (${device.device_id ? capitalizeFirstLetter(device.device_id) : device.device_id})`,
                                            hospitalId: device.hospital_id,
                                        }))}
                                        className="basic-single"
                                        classNamePrefix="select"
                                        placeholder="Select The Device ID"
                                        onChange={handleDeviceChange}
                                        required
                                    />
                                </div>
                                <div className="mb-4">
                                    <label
                                        htmlFor="nurse"
                                        className="block mb-1"
                                    >
                                        {t('management.assign.select_nurse')}
                                    </label>
                                    <div className="border-none text-xs mb-1 text-secondary font-bold">
                                        {t(
                                            'management.assign.select_nurse_dis'
                                        )}
                                    </div>
                                    <Select
                                        id="nurse"
                                        name="nurse"
                                        options={filteredNurseData.map((nurse) => ({
                                            value: nurse.uuid,
                                            label: `${nurse.name} (${nurse.nurse_id ? capitalizeFirstLetter(nurse.nurse_id) : nurse.nurse_id})`,
                                            tenantId: nurse.tenant_id,
                                            userId: nurse.user_id,
                                        }))}
                                        value={selectedNurses.map((nurse) => ({
                                            value: nurse.uuid,
                                            label: `${nurse.name} (${nurse.nurse_id ? capitalizeFirstLetter(nurse.nurse_id) : nurse.nurse_id})`,
                                            tenantId: nurse.tenant_id,
                                            userId: nurse.user_id,
                                        }))}
                                        className="basic-single"
                                        classNamePrefix="select"
                                        placeholder="Select The Nurse"
                                        onChange={handleNurseChange}
                                        isMulti
                                        closeMenuOnSelect={false}
                                        required
                                        isLoading={loading}
                                    />
                                </div>
                                <button
                                    type="submit"
                                    className="text-white bg-primary rounded-lg py-2 px-4 my-3"
                                >
                                    {t('management.assign.submit')}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
            {loading && <DataLoader />}
        </>
    );
};

export default AssignModalPopUp;
